<template>
    <div class="mb-6" flat v-if="hasAdditionalInformationRequests">
        <div class="pa-0">
            <div
                v-for="(request, index) in displayedRequests"
                :key="request.id || index"
                class="request-wrapper mb-2"
            >
                <div class="request-panel-card">
                    <div
                        class="request-card-header"
                        :class="{ 'request-card-header--clickable': expandable }"
                        @click="expandable && toggleRequestExpanded(request, index)"
                    >
                        <div class="request-card-content">
                            <h3 class="request-title">
                                {{ $t('dueDiligence.additionalInformationRequested') }}
                                <span class="request-date">({{ formatDate(request.createdAt) }})</span>
                            </h3>
                            <p
                                v-if="!expandable || !isRequestExpanded(request, index)"
                                class="request-description request-description--preview"
                            >
                                {{ getRequestPreview(request) }}
                            </p>
                        </div>
                        <v-icon
                            v-if="expandable"
                            class="request-toggle-icon"
                            :class="{ 'request-toggle-icon--expanded': isRequestExpanded(request, index) }"
                        >
                            mdi-chevron-down
                        </v-icon>
                    </div>

                    <v-expand-transition>
                        <div
                            v-show="!expandable || isRequestExpanded(request, index)"
                            class="request-card-body"
                        >
                            <p class="request-description">
                                {{ request.description || $t('dueDiligence.noDescriptionProvided') }}
                            </p>

                            <div
                                v-if="getRequestAttachments(request).length"
                                class="request-attachments mt-3"
                            >
                                <div class="request-attachments__label mb-2">
                                    {{ $t('dueDiligence.attachedDocuments') }} ({{ getRequestAttachments(request).length }})
                                </div>
                                <div class="request-attachments__list">
                                    <button
                                        v-for="(file, fileIndex) in getRequestAttachments(request)"
                                        :key="file.id || file.filePath || fileIndex"
                                        type="button"
                                        class="request-attachment-chip"
                                        @click="openAttachment(file)"
                                    >
                                        <v-icon x-small class="mr-1">mdi-paperclip</v-icon>
                                        <span class="request-attachment-chip__name text-truncate">{{ file.fileName }}</span>
                                        <v-icon x-small class="ml-1">mdi-open-in-new</v-icon>
                                    </button>
                                </div>
                            </div>

                            <div
                                v-if="getFarmRevisionRequests(request).length"
                                class="request-farm-revisions mt-3"
                            >
                                <div class="request-attachments__label mb-2">
                                    {{ $t('dueDiligence.farmsPendingRevisionBatch') }} ({{ getFarmRevisionRequests(request).length }})
                                </div>
                                <v-simple-table dense class="request-farm-revisions__table">
                                    <template v-slot:default>
                                        <thead>
                                            <tr>
                                                <th>Farm</th>
                                                <th>ID</th>
                                                <th>Action</th>
                                                <th>Comments</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr
                                                v-for="(farm, farmIndex) in getFarmRevisionRequests(request)"
                                                :key="farm.productionPlaceId || farm.farmId || farmIndex"
                                            >
                                                <td>{{ farm.farmName }}</td>
                                                <td>{{ farm.farmerRegId || '—' }}</td>
                                                <td>{{ farm.requestType === 'revision' ? $t('dueDiligence.revisionRequired') : $t('dueDiligence.uploadRequired') }}</td>
                                                <td>{{ farm.comment || '—' }}</td>
                                            </tr>
                                        </tbody>
                                    </template>
                                </v-simple-table>
                            </div>

                            <div
                                v-if="!showOnlyLatest && request.farmChanges && hasFarmChanges(request.farmChanges)"
                                class="supplier-updates-content mt-4"
                            >
                                <h4 class="supplier-updates-title">
                                    {{ $t('dueDiligence.supplierUpdates') }}
                                    <span class="supplier-updates-date">
                                        ({{ formatDate(request.farmChanges.sessionStartAt || request.updatedAt) }})
                                    </span>
                                </h4>

                                <p class="supplier-updates-description">
                                    {{ $t('dueDiligence.supplierHasRespondedAndMadeUpdates') }}
                                </p>

                                <div class="farm-changes-links">
                                    <a
                                        v-if="request.farmChanges.removed && request.farmChanges.removed.length > 0"
                                        @click.prevent="showFarmDetails(request.farmChanges.removed, 'removed')"
                                        class="farm-change-link"
                                    >
                                        {{ $t('dueDiligence.removedFarms') }} ({{ request.farmChanges.removed.length }})
                                    </a>

                                    <a
                                        v-if="request.farmChanges.updated && request.farmChanges.updated.length > 0"
                                        @click.prevent="showFarmDetails(request.farmChanges.updated, 'updated')"
                                        class="farm-change-link"
                                    >
                                        {{ $t('dueDiligence.updatedFarms') }} ({{ request.farmChanges.updated.length }})
                                    </a>

                                    <a
                                        v-if="request.farmChanges.added && request.farmChanges.added.length > 0"
                                        @click.prevent="showFarmDetails(request.farmChanges.added, 'added')"
                                        class="farm-change-link"
                                    >
                                        {{ $t('dueDiligence.addedNewFarms') }} ({{ request.farmChanges.added.length }})
                                    </a>
                                </div>
                            </div>
                        </div>
                    </v-expand-transition>
                </div>
            </div>
        </div>

        <!-- Farm Details Dialog -->
        <FarmChangesTable :dialog="showFarmDialog" :farms="selectedFarms" :type="selectedFarmType"
            :diligenceId="diligenceId" @close="showFarmDialog = false" />
    </div>
</template>

<script>
import DeforestationService from "@/_services/DeforestationService";
import S3UploadService from "@/_services/S3UploadService";
import FarmChangesTable from "./FarmChangesTable.vue";
import LoadingMixin from "@/mixins/LoadingMixin";

export default {
    mixins: [LoadingMixin],
    name: 'AdditionalInformationChangesLog',
    components: {
        FarmChangesTable
    },
    props: {
        diligenceId: {
            type: [Number, String],
            required: true
        },
        showOnlyLatest: {
            type: Boolean,
            default: false
        },
        expandable: {
            type: Boolean,
            default: true
        },
    },
    data() {
        return {
            additionalInfoRequests: [],
            farmChanges: null,
            showFarmDialog: false,
            selectedFarms: [],
            selectedFarmType: '',
            expandedRequestIds: {},
        };
    },
    computed: {
        hasAdditionalInformationRequests() {
            return this.additionalInfoRequests.length > 0;
        },
        displayedRequests() {
            if (this.showOnlyLatest) {
                return this.additionalInfoRequests.slice(0, 1);
            }
            return this.additionalInfoRequests;
        },
        dialogTitle() {
            const typeMap = {
                'removed': this.$t('dueDiligence.farmsRemoved'),
                'updated': this.$t('dueDiligence.farmsUpdated'),
                'added': this.$t('dueDiligence.farmsAdded')
            };
            return typeMap[this.selectedFarmType] || '';
        }
    },
    watch: {
        diligenceId: {
            immediate: true,
            handler(newId) {
                if (newId) {
                    this.loadAdditionalInformationRequests();
                }
            }
        }
    },
    methods: {
        getRequestKey(request, index) {
            return request?.id != null ? String(request.id) : `index-${index}`;
        },
        isRequestExpanded(request, index) {
            const key = this.getRequestKey(request, index);
            if (this.expandedRequestIds[key] === undefined) {
                return index === 0;
            }
            return !!this.expandedRequestIds[key];
        },
        toggleRequestExpanded(request, index) {
            const key = this.getRequestKey(request, index);
            this.$set(this.expandedRequestIds, key, !this.isRequestExpanded(request, index));
        },
        getRequestPreview(request) {
            const description = (request?.description || '').trim();
            if (description) return description;
            const farmCount = this.getFarmRevisionRequests(request).length;
            if (farmCount > 0) {
                return `${farmCount} farm${farmCount === 1 ? '' : 's'} update`;
            }
            return this.$t('dueDiligence.noDescriptionProvided');
        },
        async loadAdditionalInformationRequests() {
            if (!this.diligenceId) return;

            // No full-page overlay: this block stays hidden until there are requests to show.
            try {
                // Get additional information requests
                const reportResponse = await DeforestationService.getDiligenceDetail(parseInt(this.diligenceId));
                const requests = reportResponse?.data?.getDiligenceDetail?.requestAdditionalInformation || [];

                // Get farm changes (this returns changes since the last request)
                const sortedRequests = [...requests].sort((a, b) => {
                    return new Date(b.createdAt) - new Date(a.createdAt);
                });
                const lastRequest = sortedRequests.length > 0 ? sortedRequests[0] : null;
                const sessionStartAt = lastRequest ? lastRequest.createdAt : null;

                let farmChanges = null;
                // Farm changes are only attached to the latest request.
                if (lastRequest) {
                    try {
                        const changesResponse = await DeforestationService.getAdditionalInfoFarmChanges(
                            this.diligenceId,
                            sessionStartAt
                        );
                        farmChanges = changesResponse?.data?.getAdditionalInfoFarmChanges || null;
                    } catch (error) {
                        console.error('Error loading farm changes:', error);
                    }
                }

                // Map requests and attach farm changes to the most recent request

                // Attach farm changes to the most recent request (if any)
                this.additionalInfoRequests = sortedRequests.map((request, index) => {
                    // Only attach farm changes to the most recent request
                    return {
                        ...request,
                        farmChanges: index === 0 ? farmChanges : null
                    };
                });

                // Expand the first request by default when expandable
                if (this.expandable && this.additionalInfoRequests.length > 0) {
                    const first = this.additionalInfoRequests[0];
                    const key = this.getRequestKey(first, 0);
                    this.$set(this.expandedRequestIds, key, true);
                }
            } catch (error) {
                console.error('Error loading additional information requests:', error);
                this.additionalInfoRequests = [];
            }
        },
        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            const day = date.getDate().toString().padStart(2, '0');
            const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
        },
        hasFarmChanges(farmChanges) {
            if (!farmChanges) return false;
            const hasRemoved = farmChanges.removed && farmChanges.removed.length > 0;
            const hasUpdated = farmChanges.updated && farmChanges.updated.length > 0;
            const hasAdded = farmChanges.added && farmChanges.added.length > 0;
            return hasRemoved || hasUpdated || hasAdded;
        },
        showFarmDetails(farms, type) {
            this.selectedFarms = farms || [];
            this.selectedFarmType = type;
            this.showFarmDialog = true;
        },
        getRequestAttachments(request) {
            const raw = request?.attachments;
            if (!raw) return [];
            const list = Array.isArray(raw) ? raw : [];
            return list
                .map((item, index) => {
                    if (!item) return null;
                    if (typeof item === 'string') {
                        return {
                            id: `attachment-${index}`,
                            fileName: this.getFileNameFromPath(item),
                            filePath: item,
                            fileUrl: item,
                        };
                    }
                    const filePath = item.filePath || item.file_path || '';
                    const fileUrl = item.fileUrl || item.file_url || filePath;
                    const fileName = item.fileName || item.file_name || this.getFileNameFromPath(filePath || fileUrl);
                    if (!fileName && !filePath && !fileUrl) return null;
                    return {
                        id: item.id || filePath || fileUrl || `attachment-${index}`,
                        fileName,
                        filePath,
                        fileUrl,
                    };
                })
                .filter(Boolean);
        },
        getFarmRevisionRequests(request) {
            const raw = request?.farmRevisionRequests;
            if (!raw) return [];
            return Array.isArray(raw) ? raw.filter(Boolean) : [];
        },
        getFileNameFromPath(path = '') {
            return String(path).split('/').pop() || 'document';
        },
        async openAttachment(file) {
            try {
                const directUrl = file?.fileUrl;
                if (directUrl && /^https?:\/\//i.test(directUrl)) {
                    window.open(directUrl, '_blank');
                    return;
                }
                const key = file?.filePath;
                if (!key) return;
                const url = await S3UploadService.getFileUrl(key);
                window.open(url, '_blank');
            } catch (error) {
                console.error('Failed to open attachment', error);
                this.$notify?.({
                    text: this.$t('somethingWentWrong'),
                    type: 'error',
                });
            }
        },
        async refreshAdditionalInformationRequests() {
            await this.loadAdditionalInformationRequests();
        }
    }
};
</script>

<style lang="scss" scoped>
.request-wrapper {
    position: relative;
    margin-top: 20px;
}

.request-panel-card {
    background-color: #FFF8E1;
    border: 2px solid #FFB74D;
    border-radius: 8px;
    overflow: hidden;
}

.request-card-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 24px;
    background-color: #FFF8E1;
}

.request-card-header--clickable {
    cursor: pointer;
    user-select: none;
}

.request-card-body {
    padding: 0 24px 24px;
    background-color: #FFF8E1;
}

.request-toggle-icon {
    flex-shrink: 0;
    margin-top: 4px;
    color: #424242;
    transition: transform 0.2s ease;
}

.request-toggle-icon--expanded {
    transform: rotate(180deg);
}

.request-card-content {
    width: 100%;
    padding-top: 12px;
}

.request-title {
    font-size: 18px;
    font-weight: 700;
    color: #000000;
    margin: 0 0 12px 0;
}

.request-date {
    font-weight: 400;
    margin-left: 8px;
}

.request-description {
    font-size: 14px;
    line-height: 1.5;
    color: #424242;
    margin: 0;
}

.request-description--preview {
    margin-top: 8px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.request-attachments__label {
    font-size: 13px;
    font-weight: 600;
    color: #424242;
}

.request-attachments__list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.request-attachment-chip {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    padding: 6px 10px;
    border: 1px solid #90caf9;
    border-radius: 999px;
    background: #fff;
    color: #1565c0;
    font-size: 13px;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease;

    &:hover {
        background: #e3f2fd;
        border-color: #42a5f5;
    }
}

.request-attachment-chip__name {
    max-width: 220px;
}

.request-farm-revisions__table {
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    overflow: hidden;
}

.supplier-updates-content {
    background-color: #FFF8E1;
}

.supplier-updates-title {
    font-size: 16px;
    font-weight: 700;
    color: #000000;
    margin: 0 0 8px 0;
}

.supplier-updates-date {
    font-weight: 400;
    margin-left: 8px;
}

.supplier-updates-description {
    font-size: 14px;
    line-height: 1.5;
    color: #424242;
    margin: 0 0 16px 0;
}

.farm-changes-links {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.farm-change-link {
    color: #1976D2;
    text-decoration: underline;
    cursor: pointer;
    font-size: 14px;
    font-weight: 400;
    display: inline-block;
    width: fit-content;
    transition: color 0.2s;

    &:hover {
        color: #1565C0;
    }

    &:active {
        color: #0D47A1;
    }
}
</style>
